import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ThumbsDown } from 'lucide-react';

interface MarcarNaoFeitoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  numeroPedido: string;
  razaoSocial: string;
  consultorNome: string;
  /** Quem tinha marcado "já fiz" — o sinal que está sendo derrubado. */
  sinalizadoPor: string | null;
  isLoading: boolean;
  onConfirm: (recado: string) => void;
}

/**
 * O BKO conferiu e a biometria NÃO saiu (Douglas, 19/08/2026): derruba o 🖐 do
 * comercial e manda o recado junto — o consultor recebe notificação e o texto
 * fica na timeline do pedido. Recado é opcional: às vezes só marcar já resolve.
 */
export function MarcarNaoFeitoDialog({
  open,
  onOpenChange,
  numeroPedido,
  razaoSocial,
  consultorNome,
  sinalizadoPor,
  isLoading,
  onConfirm,
}: MarcarNaoFeitoDialogProps) {
  const [recado, setRecado] = useState('');

  useEffect(() => {
    if (!open) setRecado('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !isLoading && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ThumbsDown className="h-5 w-5 text-primary" />
            Biometria não foi feita — Pedido {numeroPedido}
          </DialogTitle>
          <DialogDescription>
            {razaoSocial}
            {sinalizadoPor ? ` · ${sinalizadoPor} tinha marcado como feito` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-1">
          <Label htmlFor="bio-recado" className="text-xs font-semibold">
            Recado para {consultorNome || 'o consultor'} (opcional)
          </Label>
          <Textarea
            id="bio-recado"
            value={recado}
            onChange={(e) => setRecado(e.target.value)}
            placeholder="Ex.: gestor não recebeu o link, número errado no cadastro, cliente pediu para reenviar amanhã…"
            rows={3}
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">
            O pedido continua na fila com o alerta de <strong>não feito</strong>. O consultor
            recebe a notificação e o recado fica na timeline do pedido.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button type="button" size="sm" onClick={() => onConfirm(recado)} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Marcando…
              </>
            ) : (
              <>
                <ThumbsDown className="mr-1 h-3.5 w-3.5" /> Marcar como não feito
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
